import { useShowSnackbar } from '@chilipiper/design-system'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { AssignmentType } from '@chilipiper/api-client/src/distributed-records-reporting'
import {
  ProductFeatureWithRoundRobin,
  UsersOnVacationHandling,
} from '@chilipiper/api-client/src/distribution'
import * as R from 'remeda'
import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useApiClient } from '@chilipiper/api-client/src/context'
import { isDefined } from '@chilipiper/utils'
import {
  useDistributionWithExtraDataLoader,
  usePublishedDistributionLoader,
  useHistoricalDistributionLoader,
} from '../loaders'
import {
  DistributionData,
  useDistributionCreated,
  useDistributionDuplicated,
  useDistributionPublished,
  useDistributionRemoved,
  useDistributionUpdated,
  useDistributionsByIdsQuery,
  useDistributionsListQuery,
  useDistributionsWithExtraDataByIdsQuery,
  useGatheredDistributionsByIdsQuery,
  useGetSingleDistribution,
  useGetSingleDistributionDraft,
  useGetSingleGatheredDistribution,
  usePublishedDistributionsListQuery,
  useSingleDistribution,
  useSingleDistributionDraft,
  useSingleGatheredDistribution,
} from './queries'
import {
  createDistribution,
  deleteDistribution,
  duplicateDistribution,
  featchPublishedDistributions,
  findDistributions,
  getDistribution,
  getDistributionDraft,
  getDistributionWithExtraData,
  getGatheredDistribution,
  getHistoricalDistributions,
  getHistoricalDistributionsByIds,
  publishDistribution,
  updateDistribution,
  getGetOrderedListOfUsers,
} from './services'

const PAGE_SIZE = 15

export const useDistributionsQuery = (
  tenantId: string,
  workspaceId: string,
  query?: { name?: string; assignmentType?: AssignmentType }
) => {
  const { distribution: client } = useApiClient()
  const {
    queryData: { distributions, ...queryData },
    ...hookData
  } = useDistributionsListQuery(
    {
      tenantId,
      workspaceId,
    },
    [...Object.values(query ?? {})],
    (page = 0) =>
      findDistributions({
        client,
        tenantId,
        workspaceId,
        pageSize: PAGE_SIZE,
        page,
        query,
      })
  )
  return {
    queryData: {
      ...queryData,
      distributions: distributions
        ? {
            ...distributions,
            list: distributions.list,
            total: distributions.list.length,
          }
        : undefined,
    },
    ...hookData,
    pageSize: PAGE_SIZE,
  }
}

export const usePublishedDistributionsList = (
  tenantId: string,
  workspaceId: string,
  search: string,
  assignmentType?: 'Meeting' | 'Record' | 'Conversation'
) => {
  const { distribution: client } = useApiClient()
  const {
    queryData: { distributions, isError, isLoading },
    fetchMore,
  } = usePublishedDistributionsListQuery(
    {
      tenantId,
      workspaceId,
    },
    [assignmentType ?? 'All', search],
    (page = 0) =>
      featchPublishedDistributions({
        client,
        tenantId,
        workspaceId,
        pageSize: PAGE_SIZE,
        page,
        search,
        assignmentType,
      }).then(data => ({
        ...data,
        page,
        pageSize: PAGE_SIZE,
      }))
  )
  return {
    total: distributions?.total ?? 0,
    fetchMore,
    isLoading,
    isError,
    distributions: distributions?.list,
  }
}

export const usePublishedDistributionsCount = (tenantId: string, workspaceId: string) => {
  const { distribution: client } = useApiClient()
  const {
    queryData: { distributions, isError, isLoading },
  } = usePublishedDistributionsListQuery(
    {
      tenantId,
      workspaceId,
    },
    [],
    (page = 0) =>
      featchPublishedDistributions({
        client,
        tenantId,
        workspaceId,
        pageSize: 1,
        page,
        search: '',
      }).then(data => ({
        ...data,
        page,
        pageSize: 1,
      }))
  )
  return {
    count: distributions?.total,
    isLoading,
    isError,
  }
}

//TODO this should load published distribution, it should not return draft data or type
export const useDistributionQuery = (tenantId: string, id: string) => {
  const loader = usePublishedDistributionLoader()

  return useSingleDistribution(
    {
      tenantId,
    },
    id,
    () =>
      getDistribution({
        loader,
        tenantId,
        id,
      })
  )
}

export const useDistributionHasCapping = (distributionId: string) => {
  const { tenantId } = useFireSessionContext()
  const { distribution, isLoading, isError } = useDistributionQuery(tenantId, distributionId)

  const hasHasCapping = useMemo(() => {
    if (distribution?.currentData.capping?.userCaps) {
      return distribution.currentData.capping?.userCaps.some(cap => !!cap.limit)
    }
    return false
  }, [distribution])

  return {
    hasHasCapping: !!hasHasCapping,
    isLoading,
    isError,
  }
}

export const useDistributionPreview = (id: string, workspaceId: string) => {
  const { tenantId } = useFireSessionContext()
  const loader = useHistoricalDistributionLoader()

  const {
    data: distribution,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      'distributions',
      'distribution-history',
      'distribution-preview',
      tenantId,
      workspaceId,
      id,
    ],
    queryFn: () => loader({ tenantId, workspaceId, id }),
  })

  return {
    isLoading,
    isError,
    distribution: useMemo(() => {
      if (distribution) {
        return {
          name: distribution.name,
          id: distribution.distributionId,
          team: distribution.teamRef,
        }
      }
    }, [distribution]),
  }
}

export const useDistributionGatheredQuery = (id: string) => {
  const { tenantId } = useFireSessionContext()
  const { distribution: client } = useApiClient()

  return useSingleGatheredDistribution(
    {
      tenantId,
    },
    id,
    () => {
      return getGatheredDistribution({
        client,
        tenantId,
        id,
      })
    }
  )
}

export const useGatheredDistributionsByIds = (ids: string[]) => {
  const { tenantId } = useFireSessionContext()
  const { distribution: client } = useApiClient()

  return useGatheredDistributionsByIdsQuery(
    {
      tenantId,
    },
    ids,
    id => {
      return getGatheredDistribution({
        client,
        tenantId,
        id,
      })
    }
  )
}

const useGetDistributionDraft = () => {
  const { tenantId } = useFireSessionContext()
  const { distribution: client } = useApiClient()

  return useGetSingleDistributionDraft({ tenantId }, (id: string) => {
    return getDistributionDraft({
      client,
      tenantId,
      id,
    })
  })
}

const useGetDistribution = ({ shouldThrow }: { shouldThrow?: boolean } = {}) => {
  const { tenantId } = useFireSessionContext()
  const loader = usePublishedDistributionLoader()

  return useGetSingleDistribution({ tenantId }, (id: string) => {
    return getDistribution({
      loader,
      tenantId,
      id,
      shouldThrow,
    })
  })
}

export const useGetDistributionGathered = () => {
  const { tenantId } = useFireSessionContext()
  const { distribution: client } = useApiClient()

  return useGetSingleGatheredDistribution({ tenantId }, (id: string) => {
    return getGatheredDistribution({
      client,
      tenantId,
      id,
    })
  })
}

export const useLoadDistributionTeamId = () => {
  const { tenantId } = useFireSessionContext()
  const { distribution: client } = useApiClient()

  return useCallback(
    (distributionId: string) => {
      return client.getDistributionConfig(tenantId, distributionId).then(distribution => {
        return {
          teamId: distribution.teamRef.id,
          distributionId: distribution.distributionId,
        }
      })
    },
    [tenantId, client]
  )
}

export const useDistributionsWithExtraDataByIds = (ids: string[]) => {
  const { tenantId } = useFireSessionContext()
  const loader = useDistributionWithExtraDataLoader()

  return useDistributionsWithExtraDataByIdsQuery(
    {
      tenantId,
    },
    ids,
    id => {
      return getDistributionWithExtraData({
        tenantId,
        loader,
        id,
      })
    }
  )
}

export const useDistributionDraftQuery = (id: string) => {
  const { tenantId } = useFireSessionContext()
  const { distribution: client } = useApiClient()

  return useSingleDistributionDraft(
    {
      tenantId,
    },
    id,
    () =>
      getDistributionDraft({
        client,
        tenantId,
        id,
      })
  )
}

export const useDistributionCreate = (workspaceId: string) => {
  const { tenantId } = useFireSessionContext()
  const showSnackbar = useShowSnackbar()
  const { distribution: client } = useApiClient()

  return useDistributionCreated(
    { tenantId, workspaceId },
    (config: { teamId?: string; type: AssignmentType }) =>
      createDistribution({
        client,
        tenantId,
        workspaceId,
        type: config.type,
        teamId: config.teamId,
      }).catch(error => {
        showSnackbar({ type: 'error', message: 'Could not create distribution - unknown error' })
        throw error
      })
  )
}

export const useDistributionUpdate = (workspaceId: string) => {
  const { tenantId } = useFireSessionContext()
  const showSnackbar = useShowSnackbar()
  const { distribution: client } = useApiClient()

  const getPublishedDistribution = useGetDistribution()

  return useDistributionUpdated(
    { tenantId, workspaceId },
    async (distribution: DistributionData) => {
      const publishedDistribution = await getPublishedDistribution(distribution.id)
      return updateDistribution({
        client,
        tenantId,
        distribution,
        publishedDistribution,
      }).catch(error => {
        showSnackbar({ type: 'error', message: 'Could not update distribution - unknown error' })
        throw error
      })
    }
  )
}

export const useDistributionDelete = (workspaceId: string) => {
  const { tenantId } = useFireSessionContext()
  const showSnackbar = useShowSnackbar()
  const { distribution: client } = useApiClient()

  const getDraftDistribution = useGetDistributionDraft()
  const getPublishedDistribution = useGetDistribution()

  return useDistributionRemoved({ tenantId, workspaceId }, async ({ id }) => {
    const draftDistribution = await getDraftDistribution(id)
    const publishedDistribution = await getPublishedDistribution(id)

    return deleteDistribution({
      client,
      tenantId,
      id,
      draftDistribution,
      publishedDistribution,
    }).catch(error => {
      if (error?.error?.errorCode !== 'distribution-service.distribution-in-use') {
        showSnackbar({ type: 'error', message: 'Could not delete distribution - unknown error' })
      }
      throw error
    })
  })
}

export const useDistributionDiscard = (workspaceId: string) => {
  const { tenantId } = useFireSessionContext()
  const showSnackbar = useShowSnackbar()
  const { distribution: client } = useApiClient()
  const getDraftDistribution = useGetDistributionDraft()

  return useDistributionRemoved({ tenantId, workspaceId }, async ({ id }) => {
    const draftDistribution = await getDraftDistribution(id)

    return deleteDistribution({
      client,
      tenantId,
      id,
      draftDistribution,
    }).catch(error => {
      showSnackbar({ type: 'error', message: 'Could not discard distribution - unknown error' })
      throw error
    })
  })
}

export const useDistributionDuplicate = (workspaceId: string) => {
  const { tenantId } = useFireSessionContext()
  const showSnackbar = useShowSnackbar()
  const { distribution: client } = useApiClient()

  return useDistributionDuplicated({ tenantId, workspaceId }, ({ id }) =>
    duplicateDistribution({
      client,
      tenantId,
      workspaceId,
      id,
    }).catch(error => {
      showSnackbar({ type: 'error', message: 'Could not duplicate distribution - unknown error' })
      throw error
    })
  )
}

export const useDistributionPublish = (workspaceId: string) => {
  const { tenantId } = useFireSessionContext()
  const showSnackbar = useShowSnackbar()
  const { distribution: client } = useApiClient()

  return useDistributionPublished({ tenantId, workspaceId }, ({ id }) =>
    publishDistribution({
      client,
      tenantId,
      id,
    }).catch(error => {
      showSnackbar({ type: 'error', message: 'Could not publish distribution - unknown error' })
      throw error
    })
  )
}

export const useDistributionsByIds = (ids: string[]) => {
  const { tenantId } = useFireSessionContext()
  const loader = usePublishedDistributionLoader()

  const { data, isLoading, isFetching, isError, error } = useDistributionsByIdsQuery(
    { tenantId },
    ids,
    id => {
      return getDistribution({
        loader,
        tenantId,
        id,
        shouldThrow: true,
      })
    }
  )

  const parsedData = useMemo(() => {
    const filtered = data?.filter((item): item is DistributionData => Boolean(item))
    return filtered ? R.indexBy(filtered, x => x.id) : undefined
  }, [data])

  return {
    distributions: parsedData,
    isLoading,
    isFetching,
    isError,
    error,
  }
}

export const useGetDistributionsByIds = () => {
  const getDistribution = useGetDistribution({
    shouldThrow: true,
  })

  return (ids: string[]) =>
    Promise.all(ids.map(id => getDistribution(id))).then(result => {
      const filtered = result?.filter((item): item is DistributionData => Boolean(item))
      return filtered ? R.indexBy(filtered, x => x.id) : {}
    })
}

export const useGetHistoricalDistributions = (workspaceId: string) => {
  const queryClient = useQueryClient()
  const { distribution: distributionClient } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return (filters: { since: string; until: string; assignmentType: AssignmentType }) =>
    queryClient.fetchQuery({
      queryKey: ['distributions', 'historical', tenantId, 'list'],
      queryFn: async () => {
        return getHistoricalDistributions({
          client: distributionClient,
          workspaceId,
          tenantId,
          filters,
        })
      },
    })
}

export const useGetHistoricalDistributionsByIds = (workspaceId: string) => {
  const queryClient = useQueryClient()
  const { distribution: distributionClient } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return (ids: string[], filters: { since: string; until: string }) =>
    queryClient.fetchQuery({
      queryKey: ['distributions', 'historical', tenantId, ids],
      queryFn: async () => {
        return getHistoricalDistributionsByIds({
          client: distributionClient,
          workspaceId,
          tenantId,
          ids,
          filters,
        })
      },
    })
}

export const useHistoricalDistribution = (
  workspaceId: string,
  distributionId: string,
  filters: { since: string; until: string }
) => {
  const { distribution: distributionClient } = useApiClient()
  const { tenantId } = useFireSessionContext()

  const { isError, isLoading, isFetching, data } = useQuery({
    queryKey: ['distributions', 'historical', tenantId, distributionId],
    queryFn: () => {
      return getHistoricalDistributionsByIds({
        client: distributionClient,
        workspaceId,
        tenantId,
        ids: [distributionId],
        filters,
      }).then(distributionMap => distributionMap[distributionId] ?? null)
    },
  })

  return {
    distribution: data,
    isError,
    isLoading,
    isFetching,
  }
}

export const useGetDistributionWrappedUsages = () => {
  const { tenantId } = useFireSessionContext()
  const { distribution: distributionClient } = useApiClient()
  const queryClient = useQueryClient()

  return (distributionId: string) =>
    queryClient.fetchQuery({
      queryKey: ['distributions', 'distribution', tenantId, distributionId, 'used-in'],
      queryFn: () => {
        return distributionClient.listResourcesWhichUseThisResourceMethodPut(tenantId, {
          resource: { id: distributionId },
        })
      },
    })
}

export const useDistributionLeaseInfo = (distributionId?: string) => {
  const { tenantId } = useFireSessionContext()
  const { distribution: distributionClient } = useApiClient()

  const { isError, isLoading, isFetching, data } = useQuery({
    queryKey: ['distributions', 'distribution', tenantId, distributionId, 'used-in'],
    queryFn: () => {
      return distributionClient.listResourcesWhichUseThisResourceMethodPut(tenantId, {
        resource: { id: distributionId as string },
      })
    },
    retry: false,
    enabled: Boolean(distributionId),
  })

  return {
    leaseInfo: data,
    isLoading,
    isFetching,
    isError,
  }
}

export const useDistributionCycleReset = ({
  onError = R.doNothing(),
  onSuccess = R.doNothing(),
}) => {
  const { distribution: client } = useApiClient()
  const { tenantId } = useFireSessionContext()
  const {
    mutate,
    isPending: isLoading,
    isError,
  } = useMutation({
    mutationFn: (distribution: string) =>
      client.getCurrentStatisticsOfTheDistributionGroup(tenantId, distribution).then(state =>
        client.resetDistribution(tenantId, distribution, {
          headers: { 'If-Match': `"${state.metadata.revision}"` },
        })
      ),

    onError,
    onSuccess,
  })

  return {
    reset: mutate,
    isLoading,
    isError,
  }
}

export const useGetOrderedListOfUsers = (
  tenantId: string,
  distributionId?: string,
  query?: {
    productFeature?: ProductFeatureWithRoundRobin
    vacationHandling?: UsersOnVacationHandling
  }
) => {
  const { distribution } = useApiClient()
  const { data, isInitialLoading } = useQuery({
    queryKey: ['distribution', 'ordered-list-of-users', tenantId, distributionId, query],
    queryFn: () =>
      getGetOrderedListOfUsers({ client: distribution, tenantId, id: distributionId ?? '', query }),
    enabled: Boolean(distributionId),
    gcTime: Infinity,
    retry: false,
  })

  return { data, isInitialLoading: isInitialLoading }
}

// get list of all teams that were ever part of a distribution
// for now its not historical, but it will be
export const useGetHistoricalDistributionsTeams = () => {
  const getDistribution = useGetDistribution()

  return async (distributionsIds: string[]) => {
    const distributions = await Promise.all(
      distributionsIds.map(distributionId => getDistribution(distributionId))
    )

    return R.unique(
      distributions
        .map(distribution => {
          if (
            !distribution?.currentData.teamRef?.id ||
            !distribution.currentData.firstPublishedAt
          ) {
            return
          }

          return {
            teamId: distribution.currentData.teamRef?.id,
            from: distribution.currentData.firstPublishedAt,
            to: undefined as string | undefined,
          }
        })
        .filter(isDefined)
    )
  }
}
